import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles } from '@lifeomic/chroma-react/styles/makeStyles';
import {
  ArrowLeft2,
  ArrowRight2,
  CheckCircle,
  CheckList,
  Download,
  FileText,
  Paperclip,
  Sidebar,
  Sparkle,
  TestTube,
} from '@lifeomic/chromicons';
import { alpha, lighten } from '@mui/material/styles';
import clsx from 'clsx';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { Resolution, usePDF } from 'react-to-pdf';
import { useDisableAnimations, useLocalStorageState } from '../../../../hooks';
import { ICON_MAP } from '../../../AITemplates/ImpressionTypeFilter';
import FLReverseStacked from '../../../Logos/FLReverseStacked';
import {
  PresentationAction,
  PresentationModal,
  PresentationModalProps,
} from '../../../Presentation';
import {
  AddAttachmentPopover,
  AttachmentsPopover,
} from '../AITools/Attachments/AttachmentList';
import { ViewMode } from '../AITools/Attachments/InlineAttachment';
import { EXECUTIVE_SUMMARY_IMPRESSION_TYPES } from './constants';
import {
  useExecutiveSummaryData,
  useExecutiveSummarySelection,
} from './Context';
import { CoverPage } from './CoverPage';
import { ReferenceImageMenu } from './ReferenceImageMenu';
import { useSharedStyles } from './styles';

export const useStyles = makeStyles<{
  viewMode: ViewMode;
  disableAnimations: boolean;
}>(theme => ({
  overflow: ({ viewMode }) => ({
    flex: 1,
    overflow:
      viewMode === 'presentation-expanded' ||
      viewMode === 'presentation-compact'
        ? 'auto'
        : 'visible',
    display:
      viewMode === 'presentation-expanded' ||
      viewMode === 'presentation-compact'
        ? 'flex'
        : 'block',
    borderRadius: theme.pxToRem(10),
  }),
  logo: {
    alignSelf: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    borderRadius: theme.pxToRem(10),
    padding: theme.spacing(3, 0),
    minHeight: theme.pxToRem(110),
    height: theme.pxToRem(128),
  },
  sidebar: {
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    borderRadius: theme.pxToRem(10),
    height: 'fit-content',
    minHeight: theme.pxToRem(148),
    overflowY: 'auto',
    position: 'relative',
    zIndex: 10,
    '& p': {
      color: alpha(theme.palette.common.white, 0.9),
    },
    '&:empty': {
      display: 'none',
    },
  },
  content: ({ viewMode }) => ({
    flex: 1,
    ...(viewMode === 'print' && {
      width: '83ch', // roughly fit the width of A4 paper
      '@media print': {
        '&': {
          width: 'unset',
        },
      },
    }),
  }),
  contentInner: {
    height: '100%',
    overflow: 'auto',
  },
  invisible: {
    visibility: 'hidden',
    height: 0,
    overflow: 'hidden',
  },
  navButton: {
    alignItems: 'center',
    color: theme.palette.primary[300],
    display: 'inline-flex',
    fontSize: theme.pxToRem(15),
    fontWeight: 'normal',
    gap: theme.spacing(0.5),
    lineHeight: theme.pxToRem(21),
    textTransform: 'capitalize',
    textAlign: 'left',
    maxWidth: theme.pxToRem(185),
    maxHeight: 'none',
    position: 'relative',
    whiteSpace: 'normal',
    '& > svg': {
      marginTop: theme.pxToRem(1),
      minWidth: theme.pxToRem(18),
      minHeight: theme.pxToRem(18),
      width: 18,
      height: 18,
    },
  },
  activeButton: {
    '&, &:hover, &:focus, &:focus-visible': {
      color: alpha(theme.palette.common.white, 0.9),
      backgroundColor: '#000',
      '& > svg': {
        color: theme.palette.primary[50],
      },
    },
  },
  inactiveButton: {
    backgroundColor: 'transparent',
    '&:hover, &:focus, &:focus-visible': {
      '&, & > svg': {
        color: theme.palette.primary[100],
      },
    },
  },
  pagerButton: {
    borderRadius: theme.pxToRem(99),
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    opacity: 0.75,
    minWidth: theme.pxToRem(42),
    minHeight: theme.pxToRem(42),
    maxWidth: theme.pxToRem(42),
    maxHeight: theme.pxToRem(42),
    '&:hover, &:focus': {
      opacity: 1,
    },
  },
  toggleSidebarButton: {
    transform: 'rotate(180deg)',
  },
  patientInfo: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: `${theme.spacing(0.25)} ${theme.spacing(2)}`,
    margin: theme.spacing(0.5, 0, 0.5, 0),
    '& p': {
      color: alpha(theme.palette.common.white, 0.9),
      whiteSpace: 'nowrap',
      '&:nth-child(odd)': {
        color: alpha(theme.palette.common.white, 0.65),
      },
      '&:nth-child(even)': {
        textAlign: 'right',
      },
    },
  },
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(40px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  findingsBox: ({ disableAnimations }) => ({
    // Disable animation when animations are disabled to prevent crashes
    animation: disableAnimations ? 'none' : '$fadeInUp 0.3s ease-out',
    overflowY: 'auto',
    '& > $sidebar': {
      minHeight: 0,
      paddingBottom: theme.spacing(2.25),
    },
  }),
  sidebarHeader: {
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    borderTopLeftRadius: theme.pxToRem(10),
    borderTopRightRadius: theme.pxToRem(10),
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    '& > p': {
      color: theme.palette.common.white,
    },
  },
  checkmark: {
    color: alpha(theme.palette.green[500], 0.8),
    maxHeight: theme.pxToRem(20),
    maxWidth: theme.pxToRem(20),
    minHeight: theme.pxToRem(20),
    minWidth: theme.pxToRem(20),
  },
  secondarySidebarsBox: {
    width: theme.pxToRem(262),
  },
  emptyText: {
    color: lighten(theme.palette.text.hint, 0.25),
  },
  keyboardShortcut: {
    position: 'absolute',
    left: 0,
    opacity: 0.3,
  },
  printView: {
    visibility: 'hidden',
    pointerEvents: 'none',
    '@media print': {
      '&': {
        visibility: 'visible',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100000,
      },
      // Ensure content flows across pages
      '& > div': {
        pageBreakAfter: 'auto',
        pageBreakBefore: 'auto',
      },
      '& .es-details-summary': {
        // Value obtained through experimentation. Not sure why printing cuts this off
        paddingBottom: 200,
      },
    },
  },
  hiddenOnPrint: {
    '@media print': {
      '&': {
        display: 'none',
      },
    },
  },
  printViewBackground: {
    position: 'fixed',
    top: -32,
    left: 0,
    bottom: -32,
    right: 0,
    pointerEvents: 'none',
    backgroundImage: `linear-gradient(to right, #112a70, #08a5d2 65%)`,
    width: '8.5in',
    height: '11in',
  },
}));

export const PrintView = ({ children }: { children: React.ReactNode }) => {
  const disableAnimations = useDisableAnimations();
  const classes = useStyles({ viewMode: 'print', disableAnimations });
  const { executiveSummary } = useExecutiveSummaryData();

  if (!executiveSummary) {
    return null;
  }

  const content = (
    <Box direction="column" className={clsx(classes.content)}>
      {children}
    </Box>
  );

  const printView = (
    <div className={classes.printView}>
      <style>
        {`
        @media print {
          html, body {
            width: 8.5in;
            height: initial;
            overflow: initial !important;
            -webkit-print-color-adjust: exact;
            background-color: rgb(16, 93, 172);
            background-image: linear-gradient(to right, #112a70, #08a5d2 65%);
            overflow: hidden;
          }
          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
          }
        }
        @page {
          size: letter;
          margin: 0;
          padding-top: 32px;
          padding-bottom: 32px;
        }
}`}
      </style>
      <div className={classes.printViewBackground} />
      {content}
    </div>
  );

  const Portal = createPortal(printView, document.body, 'ar-print-view');

  return (
    <>
      {Portal}
      <div className={classes.hiddenOnPrint}>{content}</div>
    </>
  );
};

const PresentationContent = ({
  step,
  patient,
  children,
}: {
  patient: fhir.Patient;

  step: string;
  children: React.ReactNode;
}) => {
  const { executiveSummary } = useExecutiveSummaryData();
  const { viewMode } = useExecutiveSummarySelection();
  const disableAnimations = useDisableAnimations();
  const classes = useStyles({ viewMode, disableAnimations });
  const sharedClasses = useSharedStyles({ viewMode });

  const childVisibility = step.includes(':cover')
    ? classes.invisible
    : classes.contentInner;

  const content = useMemo(() => {
    if (!executiveSummary) {
      return null;
    }

    if (step === 'intro:cover') {
      return (
        <CoverPage
          patient={patient}
          executiveSummary={executiveSummary}
          viewMode={viewMode}
        />
      );
    }
    if (step.includes(':cover')) {
      return (
        <CoverPage
          patient={patient}
          executiveSummary={executiveSummary}
          impressionType={step.split(':')[0]}
          viewMode={viewMode}
        />
      );
    }
    if (step === 'actionItems') {
      return (
        <>
          <Box className={sharedClasses.presentationHeader}>
            <Box align="center" gap={2}>
              <Box className={sharedClasses.impressionIconRoot}>
                <CheckList />
              </Box>
              <Box direction="column">
                <Text size="headline" className={sharedClasses.title}>
                  Action Plan
                </Text>
              </Box>
            </Box>
          </Box>
          {children}
        </>
      );
    }
    if (step === 'labResults') {
      return (
        <>
          <Box className={sharedClasses.presentationHeader}>
            <Box align="center" gap={2}>
              <Box className={sharedClasses.impressionIconRoot}>
                <TestTube />
              </Box>
              <Box direction="column">
                <Text size="headline" className={sharedClasses.title}>
                  Lab Results
                </Text>
              </Box>
            </Box>
          </Box>
          {children}
        </>
      );
    }
    return children;
  }, [
    executiveSummary,
    patient,
    step,
    children,
    sharedClasses.presentationHeader,
    sharedClasses.impressionIconRoot,
    sharedClasses.title,
    viewMode,
  ]);

  return (
    <Box direction="column" gap={1.5} key={step} className={classes.content}>
      {content}
      <Box className={childVisibility} style={{ height: 'fit-content' }}>
        {!content && children}
      </Box>
    </Box>
  );
};

interface PresentationProps extends PresentationModalProps {
  patient: fhir.Patient;
  children: React.ReactNode;
  onUseAttachment?: (attachment: any) => void;
  toPDF?: ReturnType<typeof usePDF>['toPDF'];
}

// eslint-disable-next-line @lifeomic/frontend/require-react-intl-define-messages
const messages = {
  'functional-biomarker-profile': {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.functionalBiomarkerProfile',
    defaultMessage: 'Functional Biomarker Profile',
  },
  labResults: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.labResults',
    defaultMessage: 'Lab Results',
  },
  actionItems: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.actionItems',
    defaultMessage: 'Action Plan',
  },
  cancer: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.cancer',
    defaultMessage: 'Cancer Risk',
  },
};

/**
 * This is the main component for the Executive Summary (AKA Annual Report)
 * Presentation mode.
 */
export const Presentation = (props: PresentationProps) => {
  const [step, setStep] = useState('intro:cover');
  const [showSidebars, setShowSidebars] = useLocalStorageState(
    'es-presentation-view-sidebar',
    false,
  );

  const { patient, children, onUseAttachment, ...modalProps } = props;

  const { executiveSummary } = useExecutiveSummaryData();
  const { impressionTypeFilter, viewMode, setImpressionTypeFilter } =
    useExecutiveSummarySelection();

  const disableAnimations = useDisableAnimations();
  const classes = useStyles({ viewMode, disableAnimations });
  const { formatMessage } = useIntl();

  const impressionTypes = useMemo(() => {
    // If a CRD is selected, we show all impression types
    if (executiveSummary?.compositeReportDefinitionDurableId) {
      return executiveSummary.impressions.map(i => i.impressionType);
    }
    // We only show these impression types in presentation mode, in
    // this order.
    const typesToShow = EXECUTIVE_SUMMARY_IMPRESSION_TYPES;
    // Only show types an impression has been generated for.
    return typesToShow.filter(t =>
      executiveSummary?.impressions.some(i => i.impressionType === t),
    );
  }, [
    executiveSummary?.impressions,
    executiveSummary?.compositeReportDefinitionDurableId,
  ]);

  const steps = [
    ...impressionTypes.map(t => [t + ':cover', t]).flat(),
    'actionItems:cover',
    'actionItems',
    'labResults:cover',
    'labResults',
  ];

  const changeStep = (change: -1 | 1) => {
    const stepIndex = steps.findIndex(s => s === step);
    const nextStep = steps[stepIndex + change];
    if (nextStep) {
      setStep(nextStep);
    }
  };

  const changeStepByNumber = (number: number) => {
    // jump to the cover page, starting with overview
    const targetIndex = (number - 1) * 2;
    if (targetIndex < 0 || targetIndex >= steps.length) {
      return;
    }
    const nextStep = steps[targetIndex];
    if (nextStep) {
      setStep(nextStep);
    }
  };

  // when the step changes, set the active impression to the impression type
  // (even if the step is a cover page) to pre-load the impression data
  useEffect(() => {
    const stepWithoutCover = step.replace(':cover', '');
    if (stepWithoutCover === 'actionItems') {
      setImpressionTypeFilter('actionItems');
    } else if (stepWithoutCover === 'labResults') {
      setImpressionTypeFilter('labResults');
    } else {
      const matchingImpression = executiveSummary?.impressions.find(
        i => i.impressionType === stepWithoutCover,
      )?.impressionType;
      if (matchingImpression) {
        setImpressionTypeFilter(matchingImpression);
      }
    }
  }, [
    executiveSummary?.impressions,
    impressionTypes,
    setImpressionTypeFilter,
    step,
  ]);

  const age = patient.birthDate
    ? moment().diff(patient.birthDate, 'years')
    : undefined;

  const evaluationDate = executiveSummary?.carePlan.period?.start;

  const currentImpression = useMemo(() => {
    const stepWithoutCover = step.replace(':cover', '');
    return executiveSummary?.impressions.find(
      i => i.impressionType === stepWithoutCover,
    );
  }, [executiveSummary?.impressions, step]);

  const isCoverPage = step.includes(':cover');

  // used for displaying keyboard shortcut hints
  const [isAltOrMetaPressed, setIsAltOrMetaPressed] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Alt' || e.altKey || e.metaKey || e.key === 'Meta') {
      setIsAltOrMetaPressed(true);
    }
  }, []);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Alt' || e.altKey || e.metaKey || e.key === 'Meta') {
      setIsAltOrMetaPressed(false);
    }
  }, []);

  const membershipTier = patient.meta?.tag
    ?.find(
      tag =>
        tag.system === 'http://lifeomic.com/fhir/fountain-life-membership-tier',
    )
    ?.code?.toUpperCase();

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  return (
    <PresentationModal
      {...modalProps}
      actions={
        viewMode === 'print' ? null : (
          <>
            <PresentationAction
              title={showSidebars ? 'Hide Sidebars' : 'Show Sidebars'}
              className={classes.toggleSidebarButton}
              icon={Sidebar}
              onClick={() => setShowSidebars(!showSidebars)}
            />
            <AddAttachmentPopover
              action="read-reference"
              patient={patient}
              onUseAttachment={att => onUseAttachment?.(att)}
              placement="right-start"
              anchorElement={
                <PresentationAction title="All Attachments" icon={FileText} />
              }
            />
            <AttachmentsPopover
              isLoading={false}
              action="readonly"
              allAttachments={currentImpression?.visibleAttachments ?? []}
              onUseAttachment={att => onUseAttachment?.(att)}
              placement="right-start"
              anchorElement={
                <PresentationAction
                  title="Section Attachments"
                  icon={Paperclip}
                />
              }
            />
            <ReferenceImageMenu impressionType={impressionTypeFilter} />
          </>
        )
      }
      onKeyDown={e => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          changeStep(1);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          changeStep(-1);
        } else if (e.altKey || e.metaKey) {
          // numbers start at 49
          const maybeNumber = e.keyCode - 48;
          if (maybeNumber >= 1 && maybeNumber <= 8) {
            changeStepByNumber(maybeNumber);
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }}
    >
      {(viewMode === 'presentation-expanded' ||
        viewMode === 'presentation-compact') && (
        <Box direction="column" gap={1.5}>
          <FLReverseStacked className={classes.logo} />
          <Box className={classes.sidebar} direction="column" paddingX={2}>
            <Box direction="column" gap={1} marginY={2}>
              {[...impressionTypes, 'actionItems', 'labResults'].map(
                (type, index) => (
                  <Button
                    className={clsx(
                      classes.navButton,
                      step.includes(type)
                        ? classes.activeButton
                        : classes.inactiveButton,
                    )}
                    key={type}
                    variant="text"
                    onClick={() => {
                      setStep(type);
                    }}
                    icon={ICON_MAP[type as keyof typeof messages] || Sparkle}
                  >
                    {messages[type as keyof typeof messages]
                      ? formatMessage(messages[type as keyof typeof messages])
                      : type}
                    {isAltOrMetaPressed ? (
                      <Text className={classes.keyboardShortcut} size="subbody">
                        {index + 1}
                      </Text>
                    ) : null}
                  </Button>
                ),
              )}
            </Box>
          </Box>
          <Box direction="row" justify="center" gap={1}>
            <Tooltip title="Previous">
              <IconButton
                aria-label="Previous"
                className={classes.pagerButton}
                color="inverse"
                icon={ArrowLeft2}
                onClick={() => changeStep(-1)}
                disabled={steps.indexOf(step) === 0}
              />
            </Tooltip>
            <Tooltip title="Next">
              <IconButton
                aria-label="Next"
                className={classes.pagerButton}
                color="inverse"
                icon={ArrowRight2}
                onClick={() => changeStep(1)}
                disabled={steps.indexOf(step) === steps.length - 1}
              />
            </Tooltip>
          </Box>
        </Box>
      )}
      <Box className={classes.overflow}>
        {viewMode === 'presentation-expanded' ||
        viewMode === 'presentation-compact' ? (
          <PresentationContent patient={patient} step={step}>
            {children}
          </PresentationContent>
        ) : viewMode === 'print' ? (
          <PrintView>{children}</PrintView>
        ) : null}
      </Box>
      {(viewMode === 'presentation-expanded' ||
        viewMode === 'presentation-compact') && (
        <Box gap={1.5}>
          {showSidebars && (
            <Box
              className={classes.secondarySidebarsBox}
              direction="column"
              gap={1.5}
            >
              <Box className={classes.sidebar} direction="column">
                <Box className={classes.sidebarHeader}>
                  <Text size="body">{`${patient.name?.[0]?.given?.join(' ')} ${
                    patient.name?.[0]?.family ?? ''
                  }`}</Text>
                </Box>
                <Box
                  className={classes.patientInfo}
                  paddingX={2}
                  paddingTop={1}
                  paddingBottom={1.5}
                >
                  <Text size="subbody">Age</Text>
                  <Text size="subbody">{age}</Text>
                  {evaluationDate && (
                    <>
                      <Text size="subbody">Evaluation Date</Text>
                      <Text size="subbody">
                        {moment(evaluationDate).format('MMM D YYYY')}
                      </Text>
                    </>
                  )}
                  <Text size="subbody">Membership Tier</Text>
                  <Text size="subbody">{membershipTier}</Text>
                </Box>
              </Box>
              {!isCoverPage && (
                <>
                  {currentImpression?.findings &&
                    currentImpression.findings.length > 0 && (
                      <Box className={classes.findingsBox} direction="column">
                        <Box className={classes.sidebarHeader}>
                          <Text size="body">Key Findings</Text>
                        </Box>
                        <Box
                          className={classes.sidebar}
                          style={{
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                          }}
                          direction="column"
                          paddingX={2}
                          paddingY={1.5}
                        >
                          <Box direction="column" gap={1}>
                            {currentImpression.findings.map(
                              (finding, index) => (
                                <Box key={index} gap={1}>
                                  <CheckCircle className={classes.checkmark} />
                                  <Text size="subbody">{finding}</Text>
                                </Box>
                              ),
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                </>
              )}
            </Box>
          )}
        </Box>
      )}
      {viewMode === 'print' && (
        <Box gap={1.5}>
          <Box
            className={classes.secondarySidebarsBox}
            direction="column"
            gap={1.5}
          >
            <Text size="subbody">
              Exporting to PDF may take a few seconds. Please keep this window
              open until the download is complete.
            </Text>
            <br />
            <Text size="subbody">
              Higher resolutions may fail to export for longer reports.
            </Text>
            <Button
              variant="outlined"
              aria-label="Export Low Resolution"
              icon={Download}
              title="Export Low Resolution"
              onClick={() => props.toPDF?.({ resolution: Resolution.LOW })}
            >
              Export Low Resolution
            </Button>
            {/*
              I think the naming is bad in the library. Default is Medium, and Normal is one down.
              So I'm reversing the naming here in our UI so that "Normal" is the default `medium` resolution,
              and `medium` is the next one down.
            */}
            <Button
              variant="outlined"
              aria-label="Export Medium Resolution"
              icon={Download}
              title="Export Medium Resolution"
              onClick={() => props.toPDF?.({ resolution: Resolution.NORMAL })}
            >
              Export Medium Resolution
            </Button>
            <Button
              aria-label="Export Normal Resolution"
              icon={Download}
              title="Export Normal Resolution"
              onClick={() => props.toPDF?.({ resolution: Resolution.MEDIUM })}
            >
              Export Normal Resolution
            </Button>
            <Button
              variant="outlined"
              aria-label="Export High Resolution"
              icon={Download}
              title="Export High Resolution"
              onClick={() => props.toPDF?.({ resolution: Resolution.HIGH })}
            >
              Export High Resolution
            </Button>
            <Divider />
            <Button
              variant="outlined"
              aria-label="Browser Print"
              icon={Download}
              title="Browser Print"
              onClick={() => window.print()}
            >
              Browser Print
            </Button>
          </Box>
        </Box>
      )}
    </PresentationModal>
  );
};
